/**
 * Public German grammar index pages.
 *
 *   /:lang/grammar          → GrammarHubPage   (all four levels)
 *   /:lang/grammar/:level   → GrammarLevelPage (one level's pillar grid)
 *
 * STYLE: this is the app's grammar hub, not a new design.
 * The card grid, the ghost numbers, the black icon tiles, the greyed sample
 * exercises and the centred display heading all come from
 * GrammarPillarSelector — same markup, same `.grammar-pillar-card` CSS. What is
 * dropped is only what cannot run logged out: Firestore progress, the progress
 * bar, subscriptionService's locked variant, and the exercise engine.
 *
 * An earlier version of these pages was a bare list of underlined links. It was
 * rejected, correctly, as looking like a static index page.
 */
import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useLangParam } from '../hooks/useLocalizedNavigate';
import {
  GRAMMAR_CATALOG,
  GRAMMAR_LEVELS,
  GRAMMAR_LEVEL_LABEL,
  grammarTopicsForLevel,
} from '../data/grammarCatalog';
import PublicPillarCard from '../components/grammar/PublicPillarCard';
import { useGrammarExercises } from '../components/grammar/publicGrammarData';

const BASE_URL = 'https://logami.app';

const LEVEL_BLURB = {
  a2: 'The foundations an A2 candidate is expected to control: cases, the past tenses, and the word order rules everything later depends on.',
  b1: 'What B1 examiners actually test: connectors that carry an argument, the passive, Konjunktiv II, and the case patterns that decide your grammar mark.',
  b2: 'B2 is where accuracy stops being enough and register starts counting: complex sentence linking, nominal style, and controlled variation.',
  c1: 'C1 grammar is a style question. Participle constructions, nominal versus verbal style, and the subjective modals that signal an academic voice.',
};

function useSeoHead({ title, description, canonicalUrl, ld }) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    let metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute('content') || '';
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);

    let canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute('href') || '';
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-grammar-ld', '1');
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      if (metaDesc) metaDesc.setAttribute('content', prevDesc);
      if (canonical && prevCanonical) canonical.setAttribute('href', prevCanonical);
      script.remove();
    };
  }, [title, description, canonicalUrl, ld]);
}

/** One level's grid. Loads that level's exercise bank for counts and samples. */
function LevelGrid({ level, lang, limit }) {
  const topics = grammarTopicsForLevel(level);
  const exercises = useGrammarExercises(level);
  const shown = limit ? topics.slice(0, limit) : topics;

  return (
    <div className="grammar-mobile-single-col grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
      {shown.map((topic, i) => (
        <PublicPillarCard
          key={topic.slug}
          topic={topic}
          index={i}
          lang={lang}
          exerciseCount={exercises?.[topic.pillarId]?.exerciseCount || 0}
          sample={exercises?.[topic.pillarId]?.sample}
        />
      ))}
    </div>
  );
}

/* ── /:lang/grammar ──────────────────────────────────────────────────────── */
export function GrammarHubPage() {
  const lang = useLangParam();
  const canonicalUrl = `${BASE_URL}/en/grammar`;
  const title = 'German Grammar A2–C1: Every Topic, Explained | Logami';
  const description = `All ${GRAMMAR_CATALOG.length} German grammar topics from A2 to C1, each with the rules, a worked dialogue with translations, and examples explained line by line. Free to read.`;

  useSeoHead({
    title,
    description,
    canonicalUrl,
    ld: {
      '@context': 'https://schema.org',
      '@graph': [
        { '@type': 'CollectionPage', name: 'German grammar A2–C1', description, url: canonicalUrl },
        {
          '@type': 'ItemList',
          itemListElement: GRAMMAR_LEVELS.map((level, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: `German ${GRAMMAR_LEVEL_LABEL[level]} grammar`,
            item: `${BASE_URL}/en/grammar/${level}`,
          })),
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <div
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
        data-prerender-ready="true"
      >
        <div className="text-center mb-8 sm:mb-14 px-2">
          <h1 className="font-space text-3xl sm:text-5xl md:text-6xl font-black text-black mb-3 sm:mb-4 tracking-tight leading-tight">
            German Grammar Lessons
          </h1>
          <p className="text-sm sm:text-xl text-gray-600 max-w-3xl mx-auto">
            {GRAMMAR_CATALOG.length} topics from A2 to C1, written by the teachers who build our exam
            courses. Free to read.
          </p>
        </div>

        {GRAMMAR_LEVELS.map((level) => (
          <section key={level} className="mb-12 sm:mb-16">
            <div className="text-center mb-5 sm:mb-8">
              <div className="inline-block px-3 py-1 bg-black text-white text-[10px] sm:text-xs font-bold tracking-widest uppercase rounded-full mb-3">
                {GRAMMAR_LEVEL_LABEL[level]}
              </div>
              <h2 className="font-space text-2xl sm:text-3xl font-black text-black tracking-tight mb-2">
                German {GRAMMAR_LEVEL_LABEL[level]} grammar
              </h2>
              <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                {LEVEL_BLURB[level]}
              </p>
            </div>

            <LevelGrid level={level} lang={lang} limit={3} />

            {grammarTopicsForLevel(level).length > 3 && (
              <div className="text-center mt-6">
                <Link
                  to={`/${lang}/grammar/${level}`}
                  className="inline-flex items-center justify-center min-h-[48px] px-6 py-3 bg-white text-black border-2 border-black rounded-lg text-sm font-semibold hover:bg-black hover:text-white transition-all"
                >
                  All {grammarTopicsForLevel(level).length} {GRAMMAR_LEVEL_LABEL[level]} topics
                </Link>
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

/* ── /:lang/grammar/:level ───────────────────────────────────────────────── */
export function GrammarLevelPage() {
  const { level } = useParams();
  const lang = useLangParam();
  const valid = GRAMMAR_LEVELS.includes(level);
  const topics = valid ? grammarTopicsForLevel(level) : [];
  const label = valid ? GRAMMAR_LEVEL_LABEL[level] : '';

  const canonicalUrl = `${BASE_URL}/en/grammar/${level}`;
  const title = valid
    ? `German ${label} Grammar: All ${topics.length} Topics Explained | Logami`
    : '';
  const description = valid
    ? `Every German ${label} grammar topic, each with the rules, a worked dialogue with translations and examples explained line by line. Free to read.`
    : '';

  useSeoHead({
    title,
    description,
    canonicalUrl,
    ld: {
      '@context': 'https://schema.org',
      '@graph': [
        { '@type': 'CollectionPage', name: title, description, url: canonicalUrl },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'German grammar',
              item: `${BASE_URL}/en/grammar`,
            },
            { '@type': 'ListItem', position: 2, name: `${label} grammar`, item: canonicalUrl },
          ],
        },
        {
          '@type': 'ItemList',
          itemListElement: topics.map((t, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: t.title,
            item: `${BASE_URL}/en/grammar/${t.level}/${t.slug}`,
          })),
        },
      ],
    },
  });

  if (!valid) return <Navigate to={`/${lang}/grammar`} replace />;

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <div
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
        data-prerender-ready="true"
      >
        <Link
          to={`/${lang}/grammar`}
          className="inline-flex items-center gap-1.5 min-h-[44px] font-space text-sm font-bold text-black/50 hover:text-black transition-colors duration-200 mb-4"
        >
          <ChevronLeft className="w-4 h-4" aria-hidden="true" />
          All grammar levels
        </Link>

        <div className="text-center mb-8 sm:mb-12 px-2">
          <div className="inline-block px-3 py-1 bg-black text-white text-[10px] sm:text-xs font-bold tracking-widest uppercase rounded-full mb-3">
            {label}
          </div>
          <h1 className="font-space text-3xl sm:text-5xl md:text-6xl font-black text-black mb-3 sm:mb-4 tracking-tight leading-tight">
            {label} Grammar Lessons
          </h1>
          <p className="text-sm sm:text-xl text-gray-600 max-w-3xl mx-auto">{LEVEL_BLURB[level]}</p>
        </div>

        <LevelGrid level={level} lang={lang} />

        <div className="text-center mt-12">
          <h2 className="font-space text-2xl sm:text-3xl font-black text-black tracking-tight mb-3">
            Grammar is one mark. The exam is four.
          </h2>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto mb-5">
            Reading, listening, writing and speaking all carry points too. Take a full {label} mock
            exam in the official format, on the real clock, scored the moment you finish.
          </p>
          <Link
            to={`/${lang}/try`}
            className="inline-flex items-center justify-center min-h-[48px] px-6 py-3 bg-black text-white rounded-lg text-sm font-semibold hover:bg-black/85 transition-all"
          >
            Take a free mock exam
          </Link>
        </div>
      </div>
    </div>
  );
}
